import * as z from 'zod'
import {
  postgrestFrom,
  runPostgrest,
  runPostgrestPage,
  type PostgrestPage,
} from '@/api/postgrest/client'
import {
  approvedFunnelSummarySchema,
  approvedFunnelTopicSchema,
  producttypeNameSchema,
} from '@/api/approvedFunnels/schemas'
import type {
  ApprovedFunnelSummary,
  ApprovedFunnelTopic,
  ProducttypeName,
} from '@/api/approvedFunnels/types'

/**
 * Numerischer Wert von ProducttypeTopicStatus.Approved im Backend-Enum
 * (`src/Core/Database/Panda.Core.Database/Orm/ProducttypeTopicStatus.cs`:
 * `Open = 0, Approved = 1, Ignored = 2`). PostgREST kennt keine Enum-Labels,
 * die Spalte `status` ist eine rohe Ganzzahl.
 */
const APPROVED_STATUS = 1

export interface ApprovedFunnelsQuery {
  readonly page: number
  readonly pageSize: number
}

/**
 * Freigegebene Hauptrichter, in der Datenbank nach Produkttyp aggregiert.
 *
 * Zählung und Potenzialsumme laufen über `count()` und `sum()` in der
 * Abfrage selbst, nicht im Browser. `producttype_topics` ist derzeit leer
 * (von Hand geleert), die Abfrage muss trotzdem gegen das Schema korrekt
 * sein und mit einer leeren Antwort funktionieren.
 */
export async function fetchApprovedFunnelSummaries(
  request: ApprovedFunnelsQuery,
  signal?: AbortSignal,
): Promise<PostgrestPage<readonly ApprovedFunnelSummary[]>> {
  const from = (request.page - 1) * request.pageSize
  const to = from + request.pageSize - 1

  let query = postgrestFrom('producttype_topics')
    .select(
      'producttypeKey:producttype_key,approvedTopicCount:count(),potentialSum:ranking_search_volume.sum()',
      { count: 'planned' },
    )
    .eq('status', APPROVED_STATUS)
    .order('potentialSum', { ascending: false })
    .range(from, to)
  if (signal) query = query.abortSignal(signal)

  return runPostgrestPage(query, z.array(approvedFunnelSummarySchema), 'producttype_topics')
}

/** Die freigegebenen Themen (Subtrichter) eines einzelnen Produkttyps. */
export async function fetchApprovedFunnelTopics(
  producttypeKey: string,
  signal?: AbortSignal,
): Promise<readonly ApprovedFunnelTopic[]> {
  let query = postgrestFrom('producttype_topics')
    .select(
      'id,topic,rankingPosition:ranking_position,rankingScore:ranking_score,rankingSearchVolume:ranking_search_volume,rankingMarginPerUnit:ranking_margin_per_unit,createdAt:created_at',
    )
    .eq('status', APPROVED_STATUS)
    .eq('producttype_key', producttypeKey)
    .order('ranking_search_volume', { ascending: false })
  if (signal) query = query.abortSignal(signal)

  return runPostgrest(query, z.array(approvedFunnelTopicSchema), 'producttype_topics')
}

/** Anzeigenamen für eine Menge von Produkttyp-Schlüsseln, aus den Stammdaten. */
export async function fetchProducttypeNames(
  producttypeKeys: readonly string[],
  signal?: AbortSignal,
): Promise<readonly ProducttypeName[]> {
  if (producttypeKeys.length === 0) return []

  let query = postgrestFrom('producttypes')
    .select('producttypeKey:producttypes_key,producttypeName:name')
    .in('producttypes_key', [...producttypeKeys])
  if (signal) query = query.abortSignal(signal)

  return runPostgrest(query, z.array(producttypeNameSchema), 'producttypes')
}
